import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowRight, FaCheckCircle, FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import AlertMessage from "../common/components/AlertMessage";
import Confirmation from "../common/components/Confirmation";
import RoleSelectDialog from "../common/components/RoleSelectDialog";
import OtpDigitInput from "../common/components/OtpDigitInput";
import PasswordCriteriaList from "../common/components/PasswordCriteriaList";
import { isPasswordValid } from "../common/utils/passwordCriteria";
import {
  buildForgotPasswordLockState,
  clearForgotPasswordValidatedRole,
  FORGOT_PASSWORD_OTP_ACTION_RESEND,
  FORGOT_PASSWORD_OTP_ACTION_SEND,
  FORGOT_PASSWORD_OTP_LENGTH,
  isForgotPasswordCaregiverRole,
  resetForgotLoginCode,
  resetForgotPassword,
  sendForgotPasswordOtp,
  validateForgetPasswordCredential,
  verifyForgotPasswordOtp,
} from "../common/api/forgotPasswordApi";
import { DEFAULT_MAX_OTP_SENDS } from "../common/api/familyportalOtpApi";
import { hasAuthenticatedSession } from "../common/utils/sessionContext";
import { resolveSessionLandingRoute } from "../common/utils/authSession";
import {
  formatOtpCooldownMessage,
  formatResendLockError,
  clearPersistedResendCooldown,
  clearPersistedResendLock,
  getCooldownRemainingSeconds,
  isOtpWaitMessage,
  isSendOtpRateLimitError,
  isSendOtpTooSoonError,
  readPersistedResendCooldown,
  readPersistedResendLock,
  RESEND_LOCK_STORAGE_KEY,
  resolveResendCooldownEndsAt,
  resolveResendLockEndsAt,
  writePersistedResendCooldown,
  writePersistedResendLock,
} from "../common/utils/forgotPasswordResendLock";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STEPS = {
  EMAIL: "email",
  OTP: "otp",
  VERIFY_SUCCESS: "verifySuccess",
  PASSWORD: "password",
  LOGIN_CODE: "loginCode",
  PASSWORD_SUCCESS: "passwordSuccess",
};

const VERIFY_SUCCESS_DELAY_MS = 2000;
const OTP_ERROR_CLEAR_MS = 5000;

const formatResendCooldown = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hr ${minutes} min`;
  }
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes} min ${remainder} sec`;
  }
  return `${seconds} sec`;
};

const formatForgotPasswordLockError = (data = {}, message = "") => {
  const lockState = buildForgotPasswordLockState(data, message);
  return (
    lockState.message ||
    `You've used all ${lockState.attemptsUsed} reset attempts. Please try again in ${lockState.daysRemaining} days.`
  );
};

const readInitialForgotPasswordState = (prefilledEmail = "") => {
  const normalizedPrefilledEmail = String(prefilledEmail || "")
    .trim()
    .toLowerCase();
  let emailValue = normalizedPrefilledEmail;
  let lockEndsAt = normalizedPrefilledEmail ? readPersistedResendLock(normalizedPrefilledEmail) : null;

  if (!lockEndsAt) {
    try {
      const raw = localStorage.getItem(RESEND_LOCK_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.email && parsed?.lockedUntil) {
          const lockedUntilMs = parsed.lockedUntil < 1e12 ? parsed.lockedUntil * 1000 : parsed.lockedUntil;
          if (lockedUntilMs > Date.now()) {
            emailValue = emailValue || String(parsed.email).trim().toLowerCase();
            lockEndsAt = lockedUntilMs;
          }
        }
      }
    } catch {
      // Ignore malformed lock storage.
    }
  }

  const cooldownEndsAt = emailValue ? readPersistedResendCooldown(emailValue) : null;

  return { emailValue, lockEndsAt, cooldownEndsAt };
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = (location.state?.email || "").trim().toLowerCase();
  const initialRole = (location.state?.role || "").trim().toUpperCase();
  const initialForgotPasswordState = readInitialForgotPasswordState(initialEmail);

  // Only trust this role for the email it was resolved alongside on the
  // login screen — if the user edits the email here, it no longer applies.
  const roleForEmail = (candidateEmail) => (initialRole && String(candidateEmail || "").trim().toLowerCase() === initialEmail ? initialRole : "");

  // Email + role already came from a validated login step — lock the field
  // and skip asking the user to re-enter it, but still require an explicit
  // click before sending the OTP.
  const hasPrefilledLoginEmail = Boolean(initialEmail) && EMAIL_REGEX.test(initialEmail) && Boolean(initialRole);

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState(initialForgotPasswordState.emailValue);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [confirmLoginCode, setConfirmLoginCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginCode, setShowLoginCode] = useState(false);
  const [showConfirmLoginCode, setShowConfirmLoginCode] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [resentOtp, setResentOtp] = useState(false);
  const [nextSetupStep, setNextSetupStep] = useState(null);
  const [otpMeta, setOtpMeta] = useState(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isValidatingCredential, setIsValidatingCredential] = useState(false);
  const [roleChoices, setRoleChoices] = useState([]);
  const [showRoleSelect, setShowRoleSelect] = useState(false);
  const [pendingRoleEmail, setPendingRoleEmail] = useState("");
  const [resendCooldownEndsAt, setResendCooldownEndsAt] = useState(initialForgotPasswordState.cooldownEndsAt);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(() => getCooldownRemainingSeconds(initialForgotPasswordState.cooldownEndsAt));
  const [resendLockEndsAt, setResendLockEndsAt] = useState(initialForgotPasswordState.lockEndsAt);
  const [resendLockSeconds, setResendLockSeconds] = useState(() => {
    if (!initialForgotPasswordState.lockEndsAt) {
      return 0;
    }
    return Math.max(0, Math.ceil((initialForgotPasswordState.lockEndsAt - Date.now()) / 1000));
  });
  const [showVerificationSection, setShowVerificationSection] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState("");
  const emailInputRef = useRef(null);
  const otpErrorClearTimerRef = useRef(null);

  const clearScheduledOtpError = () => {
    if (otpErrorClearTimerRef.current) {
      window.clearTimeout(otpErrorClearTimerRef.current);
      otpErrorClearTimerRef.current = null;
    }
  };

  const showOtpError = (message) => {
    clearScheduledOtpError();
    setOtpError(message);
    otpErrorClearTimerRef.current = window.setTimeout(() => {
      setOtpError("");
      otpErrorClearTimerRef.current = null;
    }, OTP_ERROR_CLEAR_MS);
  };

  const startResendCooldown = (data, emailAddress = email, message = "") => {
    const cooldownEndsAt = resolveResendCooldownEndsAt(data?.nextRequestAfter, message, data?.minRequestIntervalSeconds);
    if (!cooldownEndsAt) {
      return;
    }

    setResendCooldownEndsAt(cooldownEndsAt);
    setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
    writePersistedResendCooldown(emailAddress, cooldownEndsAt);
  };

  const startResendLock = (emailAddress, serverLockedUntil, attemptsRemaining) => {
    const lockEndsAt = resolveResendLockEndsAt({
      email: emailAddress,
      lockedUntil: serverLockedUntil,
      attemptsRemaining,
    });

    if (!lockEndsAt) {
      return;
    }

    setResendCooldownEndsAt(null);
    setResendCooldownSeconds(0);
    clearPersistedResendCooldown();
    setResendLockEndsAt(lockEndsAt);
    setResendLockSeconds(getCooldownRemainingSeconds(lockEndsAt));
    writePersistedResendLock(emailAddress, lockEndsAt);
  };

  const clearResendLock = () => {
    setResendLockEndsAt(null);
    setResendLockSeconds(0);
    clearPersistedResendLock();
  };

  const syncEmailRateLimits = (emailAddress) => {
    const normalizedEmail = String(emailAddress || "")
      .trim()
      .toLowerCase();
    if (!normalizedEmail) {
      setResendLockEndsAt(null);
      setResendLockSeconds(0);
      setResendCooldownEndsAt(null);
      setResendCooldownSeconds(0);
      return;
    }

    const persistedLock = readPersistedResendLock(normalizedEmail);
    const lockActive = Boolean(persistedLock && persistedLock > Date.now());
    setResendLockEndsAt(persistedLock);
    setResendLockSeconds(persistedLock ? getCooldownRemainingSeconds(persistedLock) : 0);

    if (lockActive) {
      setResendCooldownEndsAt(null);
      setResendCooldownSeconds(0);
      return;
    }

    const persistedCooldown = readPersistedResendCooldown(normalizedEmail);
    if (persistedCooldown) {
      setResendCooldownEndsAt(persistedCooldown);
      setResendCooldownSeconds(getCooldownRemainingSeconds(persistedCooldown));
    } else {
      setResendCooldownEndsAt(null);
      setResendCooldownSeconds(0);
    }
  };

  const applyOtpMeta = (data, emailAddress = email, message = "") => {
    if (!data) {
      return;
    }

    setOtpMeta(data);
    startResendCooldown(data, emailAddress, message);

    if (data.attemptsRemaining === 0 || data.lockedUntil) {
      startResendLock(emailAddress, data.lockedUntil, data.attemptsRemaining);
      return;
    }

    clearResendLock();
  };

  const sendsRemaining = otpMeta?.attemptsRemaining ?? DEFAULT_MAX_OTP_SENDS - 1;
  const hasSendsLeft = sendsRemaining > 0;
  const isResendCooldownActive = resendCooldownSeconds > 0;
  const isResendLockActive = resendLockSeconds > 0 || Boolean(resendLockEndsAt && resendLockEndsAt > Date.now());
  const displayedLockSeconds = resendLockSeconds > 0 ? resendLockSeconds : resendLockEndsAt ? Math.max(0, Math.ceil((resendLockEndsAt - Date.now()) / 1000)) : 0;
  const canResendOtp = hasSendsLeft && !isSendingOtp && !isResendCooldownActive && !isResendLockActive;

  const isOtpStep = step === STEPS.OTP;
  const showEmailSection = step === STEPS.EMAIL || isOtpStep;
  const showOtpSection = isOtpStep && showVerificationSection;
  const showVerifySuccessSection = step === STEPS.VERIFY_SUCCESS;
  const showPasswordSection = step === STEPS.PASSWORD;
  const showLoginCodeSection = step === STEPS.LOGIN_CODE;
  const showPasswordSuccessSection = step === STEPS.PASSWORD_SUCCESS;
  const emailLocked = isOtpStep || hasPrefilledLoginEmail;

  const setupPageLabel = nextSetupStep === STEPS.LOGIN_CODE ? "login code setup" : "password setup";

  const stepSubtitle = showPasswordSuccessSection
    ? "Your password has been updated."
    : showOtpSection
      ? ""
      : step === STEPS.EMAIL
        ? "Please enter your email address and we will send you a verification code."
        : showVerifySuccessSection
          ? "Your verification code has been confirmed."
          : showLoginCodeSection
            ? "Please enter and confirm your new login code."
            : "Please enter and confirm your new password.";
  const emailErrorText = emailError;

  const getVerifySendButtonLabel = () => (isValidatingCredential ? "Checking…" : isSendingOtp ? "Verifying and sending…" : hasPrefilledLoginEmail ? "Send verification code" : "continue");

  const getResendButtonLabel = () => {
    if (isSendingOtp) {
      return "Sending…";
    }
    if (isResendCooldownActive) {
      return `Resend (${resendCooldownSeconds}s)`;
    }
    return `Resend${otpMeta ? ` (${sendsRemaining} left)` : ""}`;
  };

  useEffect(() => {
    return () => clearScheduledOtpError();
  }, []);

  useEffect(() => {
    if (!hasAuthenticatedSession()) {
      return;
    }
    navigate(resolveSessionLandingRoute());
  }, [navigate]);

  useEffect(() => {
    if (step !== STEPS.VERIFY_SUCCESS || !nextSetupStep) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setStep(nextSetupStep);
      setNextSetupStep(null);
    }, VERIFY_SUCCESS_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [step, nextSetupStep]);

  useEffect(() => {
    syncEmailRateLimits(email);
  }, [email]);

  useEffect(() => {
    if (!resendCooldownEndsAt) {
      setResendCooldownSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((resendCooldownEndsAt - Date.now()) / 1000));
      setResendCooldownSeconds(remaining);
      if (remaining === 0) {
        setResendCooldownEndsAt(null);
        clearPersistedResendCooldown();
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendCooldownEndsAt]);

  useEffect(() => {
    if (!resendLockEndsAt) {
      setResendLockSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((resendLockEndsAt - Date.now()) / 1000));
      setResendLockSeconds(remaining);
      if (remaining === 0) {
        clearResendLock();
        setEmailError("");
        setOtpMeta(null);
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendLockEndsAt]);

  const closeAlert = () => setAlertStatus("");

  const showAlert = (message, status = "400") => {
    setAlertStatus({ message, status, origin: "center" });
  };

  const sendOtpForEmail = async (trimmedEmail, role = "") => {
    setIsSendingOtp(true);
    const result = await sendForgotPasswordOtp(trimmedEmail, FORGOT_PASSWORD_OTP_ACTION_SEND, role || roleForEmail(trimmedEmail));
    setIsSendingOtp(false);

    if (!result.success) {
      setEmail(trimmedEmail);
      clearScheduledOtpError();
      setOtpError("");

      if (result.isForgotPasswordLocked) {
        setStep(STEPS.EMAIL);
        setShowVerificationSection(false);
        setOtp("");
        setEmailError(formatForgotPasswordLockError(result.data, result.message));
        return;
      }

      if (isSendOtpRateLimitError(result.data)) {
        if (result.data) {
          setOtpMeta(result.data);
        }
        const lockEndsAt = resolveResendLockEndsAt({
          email: trimmedEmail,
          lockedUntil: result.data?.lockedUntil,
          attemptsRemaining: result.data?.attemptsRemaining ?? 0,
        });
        startResendLock(trimmedEmail, result.data?.lockedUntil, result.data?.attemptsRemaining ?? 0);
        setStep(STEPS.EMAIL);
        setShowVerificationSection(false);
        setOtp("");
        setEmailError(
          result.message ||
            formatResendLockError(lockEndsAt ? getCooldownRemainingSeconds(lockEndsAt) : displayedLockSeconds),
        );
        return;
      }

      if (isSendOtpTooSoonError(result.data, result.message) || isOtpWaitMessage(result.message)) {
        if (result.data) {
          applyOtpMeta(result.data, trimmedEmail, result.message);
        } else {
          startResendCooldown(null, trimmedEmail, result.message);
        }
        if (Number(result.data?.attemptCount) > 0) {
          setShowVerificationSection(true);
          setStep(STEPS.OTP);
          setEmailError("");
        } else {
          setStep(STEPS.EMAIL);
          setShowVerificationSection(false);
          setOtp("");
          const cooldownEndsAt = resolveResendCooldownEndsAt(
            result.data?.nextRequestAfter,
            result.message,
            result.data?.minRequestIntervalSeconds,
          );
          setEmailError(
            result.message ||
              formatOtpCooldownMessage(cooldownEndsAt ? getCooldownRemainingSeconds(cooldownEndsAt) : resendCooldownSeconds),
          );
        }
        return;
      }

      if (result.data) {
        applyOtpMeta(result.data, trimmedEmail, result.message);
      }

      setStep(STEPS.EMAIL);
      setShowVerificationSection(false);
      setOtp("");
      setEmailError(result.message);
      return;
    }

    setEmail(trimmedEmail);
    setOtp("");
    setOtpError("");
    setEmailError("");
    setResentOtp(false);
    applyOtpMeta(result.data, trimmedEmail);
    setShowVerificationSection(true);
    setStep(STEPS.OTP);
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setEmailError("");
    const trimmed = email.trim().toLowerCase();
    syncEmailRateLimits(trimmed);

    const persistedLock = readPersistedResendLock(trimmed);
    if (persistedLock && persistedLock > Date.now()) {
      setResendLockEndsAt(persistedLock);
      setEmailError(formatResendLockError(getCooldownRemainingSeconds(persistedLock)));
      return;
    }

    if (isResendLockActive) {
      setEmailError(formatResendLockError(displayedLockSeconds));
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setIsValidatingCredential(true);
    const credentialResult = await validateForgetPasswordCredential(trimmed);
    setIsValidatingCredential(false);

    if (!credentialResult.success) {
      setEmail(trimmed);
      setEmailError(credentialResult.message);
      return;
    }

    if (credentialResult.roles.length === 1) {
      await sendOtpForEmail(trimmed, credentialResult.roles[0]);
      return;
    }

    setEmail(trimmed);
    setPendingRoleEmail(trimmed);
    setRoleChoices(credentialResult.roles);
    setShowRoleSelect(true);
  };

  const handleRoleSelected = async (role) => {
    setShowRoleSelect(false);
    await sendOtpForEmail(pendingRoleEmail, role);
  };

  const closeRoleSelect = () => {
    setShowRoleSelect(false);
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    clearScheduledOtpError();
    setOtpError("");

    if (otp.length !== FORGOT_PASSWORD_OTP_LENGTH) {
      showOtpError(`Please enter a ${FORGOT_PASSWORD_OTP_LENGTH}-digit verification code.`);
      return;
    }

    setIsVerifyingOtp(true);
    const result = await verifyForgotPasswordOtp(email, otp);
    setIsVerifyingOtp(false);

    if (!result.success) {
      setOtp("");
      showOtpError(result.message);
      return;
    }

    setPassword("");
    setConfirmPassword("");
    setLoginCode("");
    setConfirmLoginCode("");
    setPasswordError("");
    clearResendLock();
    setNextSetupStep(isForgotPasswordCaregiverRole(email) ? STEPS.LOGIN_CODE : STEPS.PASSWORD);
    setStep(STEPS.VERIFY_SUCCESS);
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const persistedLock = readPersistedResendLock(normalizedEmail);
    if (persistedLock && persistedLock > Date.now()) {
      setResendLockEndsAt(persistedLock);
      setEmailError("");
      return;
    }

    setOtpError("");
    setIsSendingOtp(true);
    const result = await sendForgotPasswordOtp(email, FORGOT_PASSWORD_OTP_ACTION_RESEND, roleForEmail(email));
    setIsSendingOtp(false);

    if (!result.success) {
      if (result.data) {
        applyOtpMeta(result.data, email, result.message);
      }

      if (isSendOtpRateLimitError(result.data)) {
        startResendLock(email, result.data?.lockedUntil, result.data?.attemptsRemaining ?? 0);
        setEmailError("");
        return;
      }

      if (isSendOtpTooSoonError(result.data, result.message)) {
        setOtpError("");
        return;
      }

      showOtpError(result.message);
      return;
    }

    applyOtpMeta(result.data, email);
    setOtp("");
    setResentOtp(true);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");

    const result = step === STEPS.LOGIN_CODE ? await resetForgotLoginCode({ emailId: email, loginCode, confirmLoginCode }) : await resetForgotPassword({ emailId: email, password, confirmPassword });

    if (!result.success) {
      if (result.redirectToLogin) {
        showAlert(result.message, "400");
        window.setTimeout(() => navigate("/login", { replace: true }), 1200);
        return;
      }

      setPasswordError(result.message);
      return;
    }

    setPasswordSuccessMessage(result.message || "Password updated successfully.");
    setStep(STEPS.PASSWORD_SUCCESS);
  };

  const goToLogin = () => {
    navigate("/login", { replace: true });
  };

  const sanitizeLoginCodeInput = (value) => value.replace(/\D/g, "").slice(0, 4);

  const passwordMeetsCriteria = isPasswordValid(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmitPasswordReset = passwordMeetsCriteria && passwordsMatch;

  const requiresExitConfirmation = showOtpSection || showVerifySuccessSection || showPasswordSection || showLoginCodeSection;

  const handleBack = () => {
    if (requiresExitConfirmation) {
      setShowExitConfirm(true);
      return;
    }
    navigate("/login");
  };

  const closeExitConfirm = () => {
    setShowExitConfirm(false);
  };

  const confirmExit = (event) => {
    event.preventDefault();
    setShowExitConfirm(false);
    navigate("/login");
  };

  const handleChangeEmail = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearForgotPasswordValidatedRole(email);
    setStep(STEPS.EMAIL);
    setShowVerificationSection(false);
    setNextSetupStep(null);
    setOtp("");
    setOtpError("");
    setResentOtp(false);
    setEmailError("");
    setOtpMeta(null);
    syncEmailRateLimits(email);
    window.setTimeout(() => {
      emailInputRef.current?.focus();
      emailInputRef.current?.select();
    }, 0);
  };

  return (
    <div className="outer-container bt-login-outer bt-forgot-outer">
      {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}

      <div className="bt-login-page bt-forgot-page">
        <div className="bt-forgot-shell">
          <header className="bt-login-header bt-forgot-header">
            <div className="heading vitals-heading bt-forgot-heading" style={{ padding: "0px" }}>
              <IoChevronBackCircleSharp
                className="vitals-preview-header"
                onClick={handleBack}
                aria-label="Exit forgot password"
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleBack();
                  }
                }}
              />
              <span className="bt-forgot-heading-title">Forgot password</span>
              <span aria-hidden="true" />
            </div>
            {stepSubtitle ? <p className="bt-login-subtitle bt-forgot-subtitle">{stepSubtitle}</p> : null}
          </header>

          <div className={`bt-login-form bt-forgot-flow-form ${showPasswordSuccessSection ? "bt-forgot-flow-form--success" : ""}`}>
            {showPasswordSuccessSection ? (
              <div className="bt-forgot-success-card" role="status" aria-live="polite">
                <FaCheckCircle className="bt-forgot-success-card-icon" aria-hidden="true" />
                <h2 className="bt-forgot-success-card-title">{passwordSuccessMessage}</h2>
                <button type="button" className="bt-login-submit bt-forgot-success-card-btn" onClick={goToLogin}>
                  <span>Back to login</span>
                  <FaArrowRight aria-hidden="true" />
                </button>
              </div>
            ) : null}

            {!showPasswordSuccessSection && showEmailSection ? (
              <>
                <form onSubmit={handleEmailSubmit} className="bt-forgot-step bt-forgot-step--email">
                  <div className="bt-login-field-group">
                    <label htmlFor="forgot-email" className="bt-login-label">
                      Email
                    </label>
                    <div className="bt-login-input-wrap">
                      <FaEnvelope className="bt-login-input-icon" aria-hidden="true" />
                      <input
                        ref={emailInputRef}
                        id="forgot-email"
                        type="email"
                        name="email"
                        className="bt-login-input"
                        value={email}
                        onChange={(e) => {
                          const nextEmail = e.target.value.replace(/^\s+/, "").toLowerCase();
                          setEmail(nextEmail);
                          setEmailError("");
                          if (step === STEPS.EMAIL) {
                            setShowVerificationSection(false);
                            setOtpMeta(null);
                          }
                        }}
                        placeholder="Email"
                        autoComplete="email"
                        readOnly={emailLocked}
                        aria-readonly={emailLocked}
                        required
                      />
                    </div>
                    {emailErrorText ? (
                      <p className="bt-forgot-field-error bt-forgot-field-error--left" role="alert">
                        {emailErrorText}
                      </p>
                    ) : null}
                  </div>
                  {!isOtpStep ? (
                    <button type="submit" className="bt-login-submit" disabled={!EMAIL_REGEX.test(email.trim()) || isSendingOtp || isValidatingCredential}>
                      <span>{getVerifySendButtonLabel()}</span>
                      <FaArrowRight aria-hidden="true" />
                    </button>
                  ) : null}
                </form>
                {isOtpStep ? (
                  <button type="button" className="bt-forgot-change-email" onClick={handleChangeEmail} onMouseDown={(event) => event.preventDefault()}>
                    Use a different email
                  </button>
                ) : null}
              </>
            ) : null}

            {!showPasswordSuccessSection && showOtpSection ? (
              <form onSubmit={handleOtpSubmit} className="bt-forgot-step bt-forgot-step--otp">
                <p className="bt-forgot-step-heading">Verification code</p>
                <p className="bt-forgot-step-hint">We sent a 6-digit code to {email}.</p>
                <OtpDigitInput
                  value={otp}
                  onChange={(value) => {
                    clearScheduledOtpError();
                    setOtpError("");
                    setOtp(value);
                  }}
                  error={otpError}
                  autoFocus
                  length={FORGOT_PASSWORD_OTP_LENGTH}
                />
                {resentOtp && !otpError && !isResendCooldownActive ? (
                  <p className="bt-forgot-resent" role="status">
                    A new code has been sent.
                  </p>
                ) : null}
                <button type="submit" className="bt-login-submit" disabled={otp.length !== FORGOT_PASSWORD_OTP_LENGTH || isVerifyingOtp}>
                  <span>{isVerifyingOtp ? "Verifying…" : "Verify code"}</span>
                  <FaArrowRight aria-hidden="true" />
                </button>
                {isResendLockActive ? (
                  <p className="bt-forgot-resend-line">
                    <span className="bt-forgot-resend-cooldown" role="status">
                      Resend limit reached. You can request a new code in {formatResendCooldown(displayedLockSeconds)}.
                    </span>
                  </p>
                ) : (
                  <p className="bt-forgot-resend-line">
                    Didn&apos;t receive the email?{" "}
                    <button type="button" className="bt-forgot-resend-link" onClick={handleResendOtp} disabled={!canResendOtp || isSendingOtp} aria-live="polite">
                      {getResendButtonLabel()}
                    </button>
                  </p>
                )}
              </form>
            ) : null}

            {!showPasswordSuccessSection && showVerifySuccessSection ? (
              <div className="bt-forgot-step bt-forgot-step--verify-success" role="status" aria-live="polite">
                <FaCheckCircle className="bt-forgot-verify-success-icon" aria-hidden="true" />
                <p className="bt-forgot-verify-success-title">Verification successful</p>
                <p className="bt-forgot-verify-success-message">Navigating to {setupPageLabel} page&hellip;</p>
              </div>
            ) : null}

            {!showPasswordSuccessSection && (showPasswordSection || showLoginCodeSection) ? (
              <form onSubmit={handlePasswordSubmit} className="bt-forgot-step bt-forgot-step--email bt-forgot-step--password">
                {showLoginCodeSection ? (
                  <>
                    <div className="bt-login-field-group">
                      <label htmlFor="forgot-login-code" className="bt-login-label">
                        New login code
                      </label>
                      <div className="bt-login-input-wrap">
                        <FaLock className="bt-login-input-icon" aria-hidden="true" />
                        <input
                          id="forgot-login-code"
                          type={showLoginCode ? "text" : "password"}
                          name="loginCode"
                          className="bt-login-input bt-login-input--with-action"
                          value={loginCode}
                          onChange={(e) => {
                            setLoginCode(sanitizeLoginCodeInput(e.target.value));
                            setPasswordError("");
                          }}
                          placeholder="Enter new login code"
                          inputMode="numeric"
                          maxLength={4}
                          autoComplete="one-time-code"
                          required
                        />
                        <button type="button" className="bt-login-toggle-visibility" onClick={() => setShowLoginCode(!showLoginCode)} aria-label={showLoginCode ? "Hide login code" : "Show login code"}>
                          {showLoginCode ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                    </div>

                    <div className="bt-login-field-group">
                      <label htmlFor="forgot-confirm-login-code" className="bt-login-label">
                        Confirm new login code
                      </label>
                      <div className="bt-login-input-wrap">
                        <FaLock className="bt-login-input-icon" aria-hidden="true" />
                        <input
                          id="forgot-confirm-login-code"
                          type={showConfirmLoginCode ? "text" : "password"}
                          name="confirmLoginCode"
                          className="bt-login-input bt-login-input--with-action"
                          value={confirmLoginCode}
                          onChange={(e) => {
                            setConfirmLoginCode(sanitizeLoginCodeInput(e.target.value));
                            setPasswordError("");
                          }}
                          placeholder="Confirm new login code"
                          inputMode="numeric"
                          maxLength={4}
                          autoComplete="one-time-code"
                          required
                        />
                        <button type="button" className="bt-login-toggle-visibility" onClick={() => setShowConfirmLoginCode(!showConfirmLoginCode)} aria-label={showConfirmLoginCode ? "Hide login code" : "Show login code"}>
                          {showConfirmLoginCode ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                    </div>

                    <p className="bt-forgot-step-hint bt-forgot-login-code-note">Your login code must be 4 digits.</p>

                    {passwordError ? (
                      <p className="bt-forgot-field-error bt-forgot-field-error--left" role="alert">
                        {passwordError}
                      </p>
                    ) : null}

                    <button type="submit" className="bt-login-submit" disabled={loginCode.length !== 4 || confirmLoginCode.length !== 4}>
                      <span>Reset login code</span>
                      <FaArrowRight aria-hidden="true" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="bt-login-field-group">
                      <label htmlFor="forgot-password" className="bt-login-label">
                        New password
                      </label>
                      <div className="bt-login-input-wrap">
                        <FaLock className="bt-login-input-icon" aria-hidden="true" />
                        <input
                          id="forgot-password"
                          type={showPassword ? "text" : "password"}
                          name="password"
                          className="bt-login-input bt-login-input--with-action"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            setPasswordError("");
                          }}
                          placeholder="Enter new password"
                          autoComplete="new-password"
                          required
                        />
                        <button type="button" className="bt-login-toggle-visibility" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}>
                          {showPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      <PasswordCriteriaList password={password} />
                    </div>

                    <div className="bt-login-field-group">
                      <label htmlFor="forgot-confirm-password" className="bt-login-label">
                        Confirm password
                      </label>
                      <div className="bt-login-input-wrap">
                        <FaLock className="bt-login-input-icon" aria-hidden="true" />
                        <input
                          id="forgot-confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          className="bt-login-input bt-login-input--with-action"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            setPasswordError("");
                          }}
                          placeholder="Confirm new password"
                          autoComplete="new-password"
                          required
                        />
                        <button type="button" className="bt-login-toggle-visibility" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
                          {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      {passwordError ? (
                        <p className="bt-forgot-field-error bt-forgot-field-error--left" role="alert">
                          {passwordError}
                        </p>
                      ) : null}
                    </div>

                    <button type="submit" className="bt-login-submit" disabled={!canSubmitPasswordReset}>
                      <span>Reset password</span>
                      <FaArrowRight aria-hidden="true" />
                    </button>
                  </>
                )}
              </form>
            ) : null}
          </div>
        </div>
      </div>

      <Confirmation content="Are you sure you want to exit? Your progress will be lost." open={showExitConfirm} closeConfirmation={closeExitConfirm} processCallback={confirmExit} />
      <RoleSelectDialog open={showRoleSelect} roles={roleChoices} onSelect={handleRoleSelected} onClose={closeRoleSelect} />
    </div>
  );
};

export default ForgotPassword;
